import React, { useMemo, useState } from "react";
import PageHeader from "../../../components/layout/PageHeader";
import {
  Button,
  CustomDropdown,
  EmptyStateProps,
  Pagination,
  TabItem,
  Tabs,
  failedEmptyState,
  useLoadPhase,
} from "../../../components/ui";
// Only for the workspace that has never raised a payment — see `EmptyState`'s
// `welcome` variant, and `EMPTY_ALL_TIME.all` below.
import { ReactComponent as FirstPaymentMark } from "../../../assets/svg/No data images/revenue.svg";
import RevenueSummary from "../components/RevenueSummary";
import RevenueTable from "../components/RevenueTable";
import { DEFAULT_REVENUE_PERIOD, REVENUE_PERIODS } from "../data/revenuePeriods";
import { useRevenue } from "../hooks/useRevenue";
import type {
  RevenuePayment,
  RevenuePeriodId,
  RevenueTabId,
} from "../types/revenue.types";

const PAYMENTS_PER_PAGE = 10;

const TABS: TabItem<RevenueTabId>[] = [
  { id: "all", label: "All" },
  { id: "paid", label: "Paid" },
  { id: "pending", label: "Pending" },
  { id: "cancelled", label: "Cancelled" },
];

/** Which status each tab keeps. "All" keeps everything, so it has none. */
const TAB_STATUS: Record<RevenueTabId, RevenuePayment["status"] | null> = {
  all: null,
  paid: "Paid",
  pending: "Pending",
  cancelled: "Cancelled",
};

/** What an empty tab says, in its own words — "no payments yet" under Paid
 *  would read as a workspace that has never been paid rather than as one that
 *  has not been paid in the last month.
 *
 *  Every one of them points back at the period dropdown, because on this screen
 *  that is nearly always the reason: the rows exist, they are just outside the
 *  window the page is looking through. An empty Pending or Cancelled tab is
 *  also good news, and is worded as news rather than as a gap. */
const EMPTY_STATE: Record<RevenueTabId, EmptyStateProps> = {
  all: {
    title: "No payments raised in this period",
    description:
      "Payment requests you send to clients will be listed here. Try a longer period to see earlier ones.",
  },
  paid: {
    title: "No payments received in this period",
    description:
      "Nothing has been marked paid in this window. Widen the period to see what has come in before it.",
  },
  pending: {
    title: "No payments waiting in this period",
    description:
      "Everything raised in this window has been settled - there is nothing left to chase.",
  },
  cancelled: {
    title: "No payments cancelled in this period",
    description:
      "No request in this window was called off. Cancelled payments stay listed here for your records.",
  },
};

/** The same, with nothing outside the window to blame: under "All time" there
 *  is no longer period to try, so an empty tab is the whole answer.
 *
 *  No "all" here. An empty All tab over all time is the one case on this screen
 *  where the workspace has never raised a payment at all, and that gets the
 *  first-run welcome instead — see `firstPaymentState`. The three below are
 *  still a narrowed list reporting a result, so they stay compact. */
const EMPTY_ALL_TIME: Record<Exclude<RevenueTabId, "all">, EmptyStateProps> = {
  paid: {
    title: "No payments received yet",
    description:
      "Nothing has been marked paid. A request you confirm shows up here with the day the money came in.",
  },
  pending: {
    title: "No payments waiting",
    description:
      "Everything you have raised has been settled - there is nothing left to chase.",
  },
  cancelled: {
    title: "No payments cancelled",
    description:
      "No request has been called off. Cancelled payments stay listed here for your records.",
  },
};

/**
 * Every payment the workspace has raised, with what has come in, what is still
 * out and what was called off across the top.
 *
 * The screen reports and does not act: a payment is settled where it was raised
 * — a client's Payments tab, or the enquiry it came from — and this is the one
 * place the whole practice's money is read in a single list.
 *
 * The period dropdown decides what the whole screen is looking at, and it does
 * it at the server: the window goes up as the endpoint's `from` and `to`, and
 * both the rows and the figures come back measured over it — so a tile can
 * never be a sum of rows the table is not showing. It opens on all time, which
 * asks for no window at all.
 *
 * The tabs then narrow the table alone, in the browser, out of rows already in
 * hand: the figures are what the tabs are measured against, so they go on
 * saying the same thing whichever one is open.
 */
const RevenuePage: React.FC = () => {
  // Read before the fetch below, since it is what the fetch asks for: changing
  // it is a new question for the server, not a filter over the answer in hand.
  const [period, setPeriod] = useState<RevenuePeriodId>(DEFAULT_REVENUE_PERIOD);

  const {
    payments,
    totals,
    status: loadStatus,
    error,
    refused,
    reload,
  } = useRevenue(period);

  // A list that lands quickly goes straight from blank to filled: the table's
  // placeholders and the tiles' are only reached once the fetch has visibly
  // stalled. A period change goes through it too, so switching windows shimmers
  // rather than showing the old figures under the new one.
  const phase = useLoadPhase(loadStatus === "loading");

  const [activeTab, setActiveTab] = useState<RevenueTabId>("all");
  const [page, setPage] = useState(1);

  // Everything the window holds is already what came back, so the tabs are the
  // only narrowing left to do — and they are done here rather than by the
  // endpoint's `status`, since the rows are in hand and the three figures above
  // them have to go on counting all three states.
  const filtered = useMemo(() => {
    const status = TAB_STATUS[activeTab];
    if (!status) return payments;

    return payments.filter((payment) => payment.status === status);
  }, [payments, activeTab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAYMENTS_PER_PAGE));
  // Clamp rather than reset: a filter that shrinks the list past the current
  // page should land on the last page, not throw away the user's position.
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAYMENTS_PER_PAGE;
  const visible = filtered.slice(startIndex, startIndex + PAYMENTS_PER_PAGE);

  /** A narrower list is a different list, so it is read from the top. */
  const handleTabChange = (tab: RevenueTabId) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handlePeriodChange = (value: string) => {
    setPeriod(value as RevenuePeriodId);
    setPage(1);
  };

  /**
   * The All tab over all time with nothing in it: a practice that has not been
   * paid yet, rather than a list reporting a result. It is the only state on
   * this screen that earns the welcome layout.
   *
   * No button under it, unlike the other first-run states in the panel. This
   * screen reports and does not act: a payment is raised on the client it
   * belongs to, or on the enquiry it came from, and there is no invoice screen
   * here to send anyone to. So the lines say what will fill the list and leave
   * it at that, rather than offering an action that would land somewhere else.
   */
  const firstPaymentState = (): EmptyStateProps => ({
    variant: "welcome",
    illustration: <FirstPaymentMark aria-hidden className="w-11 h-11" />,
    title: "Your revenue starts here",
    description:
      "Once you add clients and they complete a payment, every transaction shows up on this page automatically.",
    highlights: {
      title: "What you'll see here",
      items: [
        "Create and send professional payment invoices.",
        "When a client pays, the amount is tracked here.",
        "View earnings with clear insights.",
      ],
    },
  });

  /**
   * What the table says when it has no rows to draw.
   *
   * A failure comes before emptiness: an empty table under "no payments in this
   * period" would read as an answer, when nothing was ever heard back.
   */
  const emptyState = (): EmptyStateProps => {
    // A refusal is not a failure to draw. The server turns this read down when
    // there is no plan running, and a workspace that has never been able to
    // raise a payment has never raised one — so what the screen has to report
    // is an empty ledger, which is what the welcome says. The plan itself is
    // Billing's to sell; this screen only says what it is for. See `useRevenue`.
    if (refused) return firstPaymentState();

    if (loadStatus === "failed") {
      const state = failedEmptyState(
        "We could not load your revenue",
        <Button variant="outline" size="sm" onClick={reload}>
          Try again
        </Button>
      );
      return error ? { ...state, description: error } : state;
    }

    if (period !== "all") return EMPTY_STATE[activeTab];

    return activeTab === "all"
      ? firstPaymentState()
      : EMPTY_ALL_TIME[activeTab];
  };

  return (
    // The page never scrolls; only the table inside the card does.
    <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      <PageHeader title="Revenue" />

      {/* Said as soon as the request goes out, whether or not anything is drawn
          for it: this line is not seen, so it costs nothing to be early, and
          the blocks it stands for are hidden from screen readers — they are the
          shape of an answer rather than the answer. */}
      {loadStatus === "loading" && (
        <p role="status" className="sr-only">
          Loading your revenue…
        </p>
      )}

      <div className="mt-5 flex-shrink-0">
        <RevenueSummary
          totals={totals}
          phase={phase}
          failed={loadStatus === "failed"}
        />
      </div>

      <section className="mt-4 3xl:mt-5 flex-1 min-h-0 flex flex-col overflow-hidden rounded-md border border-line bg-white">
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-6 pt-5 pb-4">
          <h2 className="text-[16px] 3xl:text-[18px] font-medium text-ink">
            Payment details
          </h2>
          <CustomDropdown
            options={REVENUE_PERIODS}
            value={period}
            onChange={handlePeriodChange}
            aria-label="Period"
            className="w-full sm:w-[180px]"
          />
        </div>

        {/* The underline under the active tab sits on top of this border rather
            than replacing it, so switching tabs does not shift the row. */}
        <div className="flex-shrink-0 border-b border-line px-4 sm:px-6">
          <Tabs tabs={TABS} activeTab={activeTab} onChange={handleTabChange} />
        </div>

        <RevenueTable
          payments={visible}
          startIndex={startIndex}
          phase={phase}
          empty={emptyState()}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setPage}
          resultCount={visible.length}
        />
      </section>
    </div>
  );
};

export default RevenuePage;
